(function () {
    // Pixels of horizontal movement before a press counts as a drag rather
    // than a click.
    const DRAG_THRESHOLD = 4;
    // Fallback for browsers without the scrollend event.
    const SETTLE_TIMEOUT = 700;

    /**
     * Grab-to-scroll for the gallery row. Touch devices scroll the row
     * natively, so only mouse pointers are handled here. Scroll snapping is
     * suspended while dragging (via the --no-snap class) because mandatory
     * snap fights direct scrollLeft writes; on release the row is scrolled to
     * the nearest snap point before snapping is restored, so re-enabling it
     * never causes a visible jump.
     *
     * @param {HTMLElement} row
     */
    function initRow(row) {
        /** @type {number | null} */
        let pointerId = null;
        let startX = 0;
        let startScrollLeft = 0;
        let dragging = false;
        let settleToken = 0;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        function nearestSnapLeft() {
            const rowRect = row.getBoundingClientRect();
            const scrollPadding =
                parseFloat(getComputedStyle(row).getPropertyValue('scroll-padding-inline-start')) || 0;
            let closest = row.scrollLeft;
            let minDistance = Infinity;

            for (const item of row.children) {
                const target = row.scrollLeft + item.getBoundingClientRect().left - rowRect.left - scrollPadding;
                const distance = Math.abs(target - row.scrollLeft);
                if (distance < minDistance) {
                    minDistance = distance;
                    closest = target;
                }
            }

            const maxLeft = row.scrollWidth - row.clientWidth;
            return Math.min(Math.max(closest, 0), maxLeft);
        }

        function settle() {
            const token = ++settleToken;

            const finish = () => {
                if (token !== settleToken) return;
                settleToken++;
                row.classList.remove('fi__row--no-snap');
                row.removeEventListener('scrollend', finish);
            };

            const target = nearestSnapLeft();
            if (Math.abs(target - row.scrollLeft) < 1) {
                finish();
                return;
            }

            row.addEventListener('scrollend', finish);
            setTimeout(finish, SETTLE_TIMEOUT);
            row.scrollTo({
                left: target,
                behavior: prefersReducedMotion.matches ? 'auto' : 'smooth'
            });
        }

        row.addEventListener('pointerdown', (event) => {
            if (event.pointerType !== 'mouse' || event.button !== 0) return;
            // A new grab takes over from a still-settling release.
            settleToken++;
            pointerId = event.pointerId;
            startX = event.clientX;
            startScrollLeft = row.scrollLeft;
            dragging = false;
        });

        row.addEventListener('pointermove', (event) => {
            if (pointerId === null || event.pointerId !== pointerId) return;

            const delta = event.clientX - startX;
            if (!dragging) {
                if (Math.abs(delta) < DRAG_THRESHOLD) return;
                dragging = true;
                row.classList.add('fi__row--dragging', 'fi__row--no-snap');
                row.setPointerCapture(pointerId);
            }

            row.scrollLeft = startScrollLeft - delta;
        });

        /** @param {PointerEvent} event */
        const endDrag = (event) => {
            if (pointerId === null || event.pointerId !== pointerId) return;
            pointerId = null;
            if (!dragging) return;
            dragging = false;
            row.classList.remove('fi__row--dragging');
            settle();
        };

        row.addEventListener('pointerup', endDrag);
        row.addEventListener('pointercancel', endDrag);

        // Native image dragging would hijack the grab gesture.
        row.addEventListener('dragstart', (event) => event.preventDefault());
    }

    function initAllRows() {
        document.querySelectorAll('.fi__row:not([data-drag-scroll])').forEach((row) => {
            if (row instanceof HTMLElement) {
                row.setAttribute('data-drag-scroll', 'true');
                initRow(row);
            }
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAllRows);
    } else {
        initAllRows();
    }

    if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:load', initAllRows);
    }
})();
